// src/services/permissionService.js
import apiService from '../utils/apiInterceptor';

const permissionService = {
  /**
   * Get all permissions
   * @returns {Promise<Object>} Promise resolving to permissions data
   */
  getAllPermissions: async () => {
    try {
      return await apiService.get('/admin/permission');
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  },

  /**
   * Get all admin permissions
   * @returns {Promise<Object>} Promise resolving to admin permissions data
   */
  getAdminPermissions: async () => {
    try {
      return await apiService.get('/admin/permission?is_admin=1');
    } catch (error) {
      console.error('Error fetching admin permissions:', error);
      throw error;
    }
  },

  /**
   * Get all non-admin permissions
   * @returns {Promise<Object>} Promise resolving to non-admin permissions data
   */
  getNonAdminPermissions: async () => {
    try {
      return await apiService.get('/admin/permission?is_admin=0');
    } catch (error) {
      console.error('Error fetching non-admin permissions:', error);
      throw error;
    }
  },

  /**
   * Get permission by ID
   * @param {number|string} id - Permission ID
   * @returns {Promise<Object>} Promise resolving to permission data
   */
  getPermissionById: async (id) => {
    try {
      return await apiService.get(`/admin/permission/${id}`);
    } catch (error) {
      console.error(`Error fetching permission with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new permission
   * @param {Object} permissionData - Permission data
   * @returns {Promise<Object>} Promise resolving to created permission
   */
  createPermission: async (permissionData) => {
    try {
      return await apiService.post('/admin/permission', permissionData);
    } catch (error) {
      console.error('Error creating permission:', error);
      throw error;
    }
  },

  /**
   * Update a permission
   * @param {number|string} id - Permission ID
   * @param {Object} permissionData - Updated permission data
   * @returns {Promise<Object>} Promise resolving to updated permission
   */
  updatePermission: async (id, permissionData) => {
    try {
      return await apiService.put(`/admin/permission/${id}`, permissionData);
    } catch (error) {
      console.error(`Error updating permission with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a permission
   * @param {number|string} id - Permission ID to delete
   * @returns {Promise<Object>} Promise resolving to the API response
   */
  deletePermission: async (id) => {
    try {
      return await apiService.delete(`/admin/permission/${id}`);
    } catch (error) {
      console.error(`Error deleting permission with ID ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Check if a permission is an admin permission
   * @param {number|string} permissionId - Permission ID
   * @returns {Promise<boolean>} Promise resolving to whether the permission is admin
   */
  isAdminPermission: async (permissionId) => {
    try {
      const permission = await permissionService.getPermissionById(permissionId);
      return permission && permission.is_admin === 1;
    } catch (error) {
      console.error(`Error checking if permission ${permissionId} is admin:`, error);
      return false;
    }
  }
};

export default permissionService;